'use client';

import { useState, useEffect } from 'react';
import {
  Menu,
  X,
  Music2,
  VolumeX,
  HelpCircle,
  Mail,
  Home,
} from 'lucide-react';

import { useTranslation } from '@/context/LanguageContext';
import ContactModal from './ContactModal';

interface HeaderProps {
  onToolSwitch: (tool: 'extractor' | 'remover') => void;
  activeTool: 'extractor' | 'remover';
}

export default function Header({ onToolSwitch, activeTool }: HeaderProps) {
  const { t, isRTL } = useTranslation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);

  const NAV_LINKS = [
    { label: t('nav_home'), href: '#hero', icon: Home },
    { label: t('nav_extractor'), href: '#tool', icon: Music2 },
    { label: t('nav_remover'), href: '#remover', icon: VolumeX },
    { label: t('nav_help'), href: '#faq', icon: HelpCircle },
    { label: t('nav_contact'), href: '#contact', icon: Mail },
  ];

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setMobileOpen(false);
      }
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, []);

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  const handleNavClick = (href: string) => {
    setMobileOpen(false);
    if (href === '#contact') {
      setContactOpen(true);
      return;
    }
    if (href === '#tool') onToolSwitch('extractor');
    if (href === '#remover') onToolSwitch('remover');
    const el = document.querySelector(href);
    if (el) {
      setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
    }
  };

  return (
    <>
      {/* ─── Sticky Header ─── */}
      <header data-component-id="src/app/audio-extractor-interface/components/Header.tsx:78:6" data-component-path="src/app/audio-extractor-interface/components/Header.tsx" data-component-line="78" data-component-end-line="163" data-component-file="Header.tsx" data-component-name="header" data-component-content="%7B%22elementName%22%3A%22header%22%7D"
        dir={isRTL ? 'rtl' : 'ltr'}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? 'bg-white/95 backdrop-blur-md shadow-soft border-b border-slate-100'
            : 'bg-white/80 backdrop-blur-sm'
        }`}
        role="banner"
      >
        <div data-component-id="src/app/audio-extractor-interface/components/Header.tsx:87:8" data-component-path="src/app/audio-extractor-interface/components/Header.tsx" data-component-line="87" data-component-end-line="138" data-component-file="Header.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22max-w-screen-xl%20mx-auto%20px-4%20sm%3Apx-6%20lg%3Apx-8%20xl%3Apx-10%202xl%3Apx-16%22%7D" className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8 xl:px-10 2xl:px-16">
          <div data-component-id="src/app/audio-extractor-interface/components/Header.tsx:88:10" data-component-path="src/app/audio-extractor-interface/components/Header.tsx" data-component-line="88" data-component-end-line="137" data-component-file="Header.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20justify-between%20h-16%20lg%3Ah-18%22%7D" className="flex items-center justify-between h-16 lg:h-18">
            {/* Logo */}
            <a data-component-id="src/app/audio-extractor-interface/components/Header.tsx:90:12" data-component-path="src/app/audio-extractor-interface/components/Header.tsx" data-component-line="90" data-component-end-line="105" data-component-file="Header.tsx" data-component-name="a" data-component-content="%7B%22elementName%22%3A%22a%22%2C%22href%22%3A%22%23hero%22%2C%22className%22%3A%22flex%20items-center%20gap-2.5%20flex-shrink-0%20group%22%7D"
              href="#hero"
              onClick={() => handleNavClick('#hero')}
              className="flex items-center gap-2.5 flex-shrink-0 group"
              aria-label="Audioextractor.online home"
            >
              <div data-component-id="src/app/audio-extractor-interface/components/Header.tsx:96:14" data-component-path="src/app/audio-extractor-interface/components/Header.tsx" data-component-line="96" data-component-end-line="104" data-component-file="Header.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20gap-2%22%7D" className="flex items-center gap-2">
                <img data-component-id="src/app/audio-extractor-interface/components/Header.tsx:97:16" data-component-path="src/app/audio-extractor-interface/components/Header.tsx" data-component-line="97" data-component-end-line="103" data-component-file="Header.tsx" data-component-name="img" data-component-content="%7B%22elementName%22%3A%22img%22%2C%22src%22%3A%22%2Fassets%2Fimages%2FIMG_0275-1773745835202.png%22%2C%22alt%22%3A%22Audioextractor.online%20logo%22%7D"
                  src="/assets/images/IMG_0275-1773745835202.png"
                  alt="Audioextractor.online logo"
                  width={135}
                  height={135}
                  style={{ width: 135, height: 135, objectFit: 'contain' }}
                />
              </div>
            </a>

            {/* Desktop Nav */}
            <nav data-component-id="src/app/audio-extractor-interface/components/Header.tsx:108:12" data-component-path="src/app/audio-extractor-interface/components/Header.tsx" data-component-line="108" data-component-end-line="123" data-component-file="Header.tsx" data-component-name="nav" data-component-content="%7B%22elementName%22%3A%22nav%22%2C%22className%22%3A%22hidden%20lg%3Aflex%20items-center%20gap-1%22%7D" className="hidden lg:flex items-center gap-1" aria-label="Main navigation">
              {NAV_LINKS.map(({ label, href, icon: NavIcon }) => (
                <button data-component-id="src/app/audio-extractor-interface/components/Header.tsx:110:16" data-component-path="src/app/audio-extractor-interface/components/Header.tsx" data-component-line="110" data-component-end-line="121" data-component-file="Header.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%7D"
                  key={href}
                  onClick={() => handleNavClick(href)}
                  className={`flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-sm font-500 transition-all duration-150 ${
                    (href === '#tool' && activeTool === 'extractor') ||
                    (href === '#remover' && activeTool === 'remover')
                      ? 'bg-violet-50 text-violet-700' : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                  }`}
                >
                  <NavIcon data-component-id="src/app/audio-extractor-interface/components/Header.tsx:119:18" data-component-path="src/app/audio-extractor-interface/components/Header.tsx" data-component-line="119" data-component-end-line="119" data-component-file="Header.tsx" data-component-name="NavIcon" data-component-content="%7B%22elementName%22%3A%22NavIcon%22%2C%22className%22%3A%22opacity-70%22%7D" size={14} className="opacity-70" />
                  {label}
                </button>
              ))}
            </nav>

            {/* Right actions */}
            <div data-component-id="src/app/audio-extractor-interface/components/Header.tsx:126:12" data-component-path="src/app/audio-extractor-interface/components/Header.tsx" data-component-line="126" data-component-end-line="136" data-component-file="Header.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20gap-2%22%7D" className="flex items-center gap-2">
              {/* Mobile menu toggle */}
              <button data-component-id="src/app/audio-extractor-interface/components/Header.tsx:128:14" data-component-path="src/app/audio-extractor-interface/components/Header.tsx" data-component-line="128" data-component-end-line="135" data-component-file="Header.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22lg%3Ahidden%20p-2%20rounded-lg%20text-slate-600%20hover%3Abg-slate-50%20hover%3Atext-slate-900%20transition-all%22%7D"
                onClick={() => setMobileOpen(!mobileOpen)}
                className="lg:hidden p-2 rounded-lg text-slate-600 hover:bg-slate-50 hover:text-slate-900 transition-all"
                aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
                aria-expanded={mobileOpen}
              >
                {mobileOpen ? <X data-component-id="src/app/audio-extractor-interface/components/Header.tsx:134:30" data-component-path="src/app/audio-extractor-interface/components/Header.tsx" data-component-line="134" data-component-end-line="134" data-component-file="Header.tsx" data-component-name="X" data-component-content="%7B%22elementName%22%3A%22X%22%7D" size={20} /> : <Menu data-component-id="src/app/audio-extractor-interface/components/Header.tsx:134:48" data-component-path="src/app/audio-extractor-interface/components/Header.tsx" data-component-line="134" data-component-end-line="134" data-component-file="Header.tsx" data-component-name="Menu" data-component-content="%7B%22elementName%22%3A%22Menu%22%7D" size={20} />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Nav Drawer */}
        <div data-component-id="src/app/audio-extractor-interface/components/Header.tsx:141:8" data-component-path="src/app/audio-extractor-interface/components/Header.tsx" data-component-line="141" data-component-end-line="162" data-component-file="Header.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D"
          className={`lg:hidden overflow-hidden transition-all duration-300 ease-in-out ${
            mobileOpen ? 'max-h-[400px] border-t border-slate-100' : 'max-h-0'
          }`}
        >
          <nav data-component-id="src/app/audio-extractor-interface/components/Header.tsx:146:10" data-component-path="src/app/audio-extractor-interface/components/Header.tsx" data-component-line="146" data-component-end-line="161" data-component-file="Header.tsx" data-component-name="nav" data-component-content="%7B%22elementName%22%3A%22nav%22%2C%22className%22%3A%22bg-white%20px-4%20py-3%20space-y-1%22%7D" className="bg-white px-4 py-3 space-y-1" aria-label="Mobile navigation">
            {NAV_LINKS.map(({ label, href, icon: NavIcon }) => (
              <button data-component-id="src/app/audio-extractor-interface/components/Header.tsx:148:14" data-component-path="src/app/audio-extractor-interface/components/Header.tsx" data-component-line="148" data-component-end-line="159" data-component-file="Header.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%7D"
                key={href}
                onClick={() => handleNavClick(href)}
                className={`flex items-center gap-3 w-full px-4 py-3 rounded-xl text-sm font-500 text-left transition-all duration-150 ${
                  (href === '#tool' && activeTool === 'extractor') ||
                  (href === '#remover' && activeTool === 'remover')
                    ? 'bg-violet-50 text-violet-700' : 'text-slate-700 hover:bg-slate-50'
                }`}
              >
                <NavIcon data-component-id="src/app/audio-extractor-interface/components/Header.tsx:157:16" data-component-path="src/app/audio-extractor-interface/components/Header.tsx" data-component-line="157" data-component-end-line="157" data-component-file="Header.tsx" data-component-name="NavIcon" data-component-content="%7B%22elementName%22%3A%22NavIcon%22%2C%22className%22%3A%22opacity-70%20flex-shrink-0%22%7D" size={16} className="opacity-70 flex-shrink-0" />
                {label}
              </button>
            ))}
          </nav>
        </div>
      </header>
      <ContactModal data-component-id="src/app/audio-extractor-interface/components/Header.tsx:164:6" data-component-path="src/app/audio-extractor-interface/components/Header.tsx" data-component-line="164" data-component-end-line="164" data-component-file="Header.tsx" data-component-name="ContactModal" data-component-content="%7B%22elementName%22%3A%22ContactModal%22%7D" isOpen={contactOpen} onClose={() => setContactOpen(false)} />
    </>
  );
}
